import React, { useState } from 'react';
import { useAccountSettings } from '../../hooks/useAccountSettings';
import { usePlaceBusinessInfo } from '../../hooks/usePlaceBusinessInfo';
import { useCafeteriaSelection } from '../../context/CafeteriaSelectionContext';
import { UpdateBusinessUserRequest, UpdatePlaceBusinessInfoRequest } from '../../types/auth';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const SettingsPage = () => {
  const { currentCafeteria } = useCafeteriaSelection();
  const { profile, loading, saving, error, updateProfile } = useAccountSettings();
  const { businessInfo, loading: businessLoading, saving: businessSaving, error: businessError, updateBusinessInfo } = usePlaceBusinessInfo(currentCafeteria?.id || null);
  
  const [formData, setFormData] = useState<UpdateBusinessUserRequest>({
    fullName: '',
    email: '',
    phoneNumber: '',
  });
  
  const [businessFormData, setBusinessFormData] = useState<UpdatePlaceBusinessInfoRequest>({
    companyName: '',
    taxId: '',
    website: '',
    businessEmail: '',
    businessPhone: '',
  });
  
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [businessSuccessMessage, setBusinessSuccessMessage] = useState<string | null>(null);

  // Atualizar form quando carregar o perfil pessoal
  React.useEffect(() => {
    if (profile) {
      setFormData({
        fullName: profile.fullName,
        email: profile.email,
        phoneNumber: profile.phoneNumber || '',
      });
    }
  }, [profile]);

  // Atualizar form quando carregar dados empresariais
  React.useEffect(() => {
    if (businessInfo) {
      setBusinessFormData({
        companyName: businessInfo.companyName || '',
        taxId: businessInfo.taxId || '',
        website: businessInfo.website || '',
        businessEmail: businessInfo.businessEmail || '',
        businessPhone: businessInfo.businessPhone || '',
      });
    }
  }, [businessInfo]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBusinessInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBusinessFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    
    const success = await updateProfile(formData);
    if (success) {
      setSuccessMessage('Dados pessoais atualizados com sucesso!');
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  };

  const handleBusinessSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusinessSuccessMessage(null);
    
    const success = await updateBusinessInfo(businessFormData);
    if (success) {
      setBusinessSuccessMessage('Dados empresariais atualizados com sucesso!');
      setTimeout(() => setBusinessSuccessMessage(null), 5000);
    }
  };

  if (loading || businessLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Configurações</h1>
        <p className="text-gray-600 mt-2">
          Gerencie suas informações pessoais e empresariais
        </p>
      </div>

      {/* Dados Pessoais */}
      <div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Dados Pessoais</h2>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
            {successMessage}
          </div>
        )}

        <div className="max-w-2xl">
          <form onSubmit={handleSubmit} className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-800">
                Suas Informações Pessoais
              </h3>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  className="input"
                  placeholder="Ex: João Silva"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="input"
                  placeholder="joao@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefone Pessoal
                </label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className="input"
                  placeholder="(11) 99999-9999"
                />
              </div>

              <div className="pt-4 border-t border-gray-100">
                <button 
                  type="submit" 
                  disabled={saving}
                  className="btn btn-primary"
                >
                  {saving ? 'Salvando...' : 'Salvar Dados Pessoais'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Dados Empresariais */}
      <div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Dados Empresariais</h2>
        
        {businessError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {businessError}
          </div>
        )}

        {businessSuccessMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
            {businessSuccessMessage}
          </div>
        )}

        <div className="max-w-2xl">
          <form onSubmit={handleBusinessSubmit} className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-800">
                Informações do Estabelecimento
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {currentCafeteria ? `Configurando: ${currentCafeteria.name}` : 'Nenhum estabelecimento selecionado'}
              </p>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Razão Social
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={businessFormData.companyName}
                  onChange={handleBusinessInputChange}
                  className="input"
                  placeholder="Ex: Café Central Ltda"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CNPJ
                </label>
                <input
                  type="text"
                  name="taxId"
                  value={businessFormData.taxId}
                  onChange={handleBusinessInputChange}
                  className="input"
                  placeholder="XX.XXX.XXX/XXXX-XX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={businessFormData.website}
                  onChange={handleBusinessInputChange}
                  className="input"
                  placeholder="https://www.empresa.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Comercial
                </label>
                <input
                  type="email"
                  name="businessEmail"
                  value={businessFormData.businessEmail}
                  onChange={handleBusinessInputChange}
                  className="input"
                  placeholder="contato@empresa.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefone Comercial
                </label>
                <input
                  type="tel"
                  name="businessPhone"
                  value={businessFormData.businessPhone}
                  onChange={handleBusinessInputChange}
                  className="input"
                  placeholder="(11) 3333-3333"
                />
              </div>

              <div className="pt-4 border-t border-gray-100">
                <button 
                  type="submit" 
                  disabled={businessSaving || !currentCafeteria}
                  className="btn btn-primary"
                >
                  {businessSaving ? 'Salvando...' : 'Salvar Dados Empresariais'}
                </button>
                {!currentCafeteria && (
                  <p className="text-sm text-gray-500 mt-2">
                    Selecione um estabelecimento para editar os dados empresariais
                  </p>
                )}
              </div>
            </div>
          </form>

          {/* Informações do Estabelecimento */}
          {businessInfo && (
            <div className="card mt-6">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-800">
                  Informações do Estabelecimento
                </h3>
              </div>
              <div className="card-body space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Nome Fantasia</span>
                  <span className="text-sm font-medium text-gray-800">{businessInfo.name}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Endereço</span>
                  <span className="text-sm font-medium text-gray-800">{businessInfo.address}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Cidade</span>
                  <span className="text-sm font-medium text-gray-800">{businessInfo.cityName}, {businessInfo.stateName}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage; 